import _ from 'underscore';

import {
  MemberFeature,
  OrganizationFeature,
  ProductDesc,
  ProductFeatureMap,
  ProductFeatureSet,
  ProductIdMap,
  ProductInterval,
  ProductNameMap,
  ProductShortName,
  ProductShortNameMap,
  ProductSpec,
  ProductSpecMap,
} from 'types';
import assert from 'util/assert';

class FeatureSet {
  products: ProductNameMap = new Map();

  // a map between product numbers and an array containing what features that product adds
  productFeatures: ProductFeatureMap = new Map();

  // a map from product short names to their ids
  productShortNames: ProductShortNameMap = new Map<ProductShortName, number>();

  // # a map from product ids to the price of the product
  prices: ProductIdMap<number> = new Map<number, number>();

  // a map from product ids to the billing interval ("monthly" or "yearly")
  intervals: ProductIdMap<ProductInterval> = new Map<number, ProductInterval>();

  // a list of which products are billed at the start of the pay period
  prebilledProducts: ProductIdMap<boolean> = new Map<number, boolean>();

  // a list of per-user products
  perUserProducts: ProductIdMap<boolean> = new Map<number, boolean>();

  perActiveUserProducts: ProductIdMap<boolean> = new Map<number, boolean>();

  // a map with products that pay with a purchase order
  purchaseOrderProducts: ProductIdMap<boolean> = new Map<number, boolean>();

  // features everyone should have enabled
  globalFeatures: ProductFeatureSet = new Set();

  // products that should ask you to upgrade to the latsest
  upgradeProducts: ProductIdMap<boolean> = new Map<number, boolean>();
  yearlyEquivalents: ProductIdMap<ProductSpec['yearlyEquivalent']> = new Map<
    number,
    ProductSpec['yearlyEquivalent']
  >();

  constructor(products: ProductSpecMap) {
    for (const [productNum, productVal] of products) {
      const product: ProductSpec = {
        interval: ProductInterval.Monthly,
        hidden: false,
        prebill: true,
        perUser: false,
        features: new Set(),
        upgrade: false,
        ...productVal,
      };

      this.products.set(productNum, product.name);

      if (!product.hidden) {
        this.productShortNames.set(productVal.shortName, productNum);
      }

      this.prebilledProducts.set(productNum, productVal.prebill || false);
      this.perUserProducts.set(productNum, productVal.perUser || false);
      this.perActiveUserProducts.set(
        productNum,
        productVal.perActiveUser || false,
      );

      this.productFeatures.set(productNum, productVal.features || []);

      if (productVal.price) {
        this.prices.set(productNum, productVal.price);
      }

      if (productVal.interval) {
        this.intervals.set(productNum, productVal.interval);
      }

      this.upgradeProducts.set(productNum, productVal.upgrade || false);

      if (productVal.yearlyEquivalent) {
        this.yearlyEquivalents.set(productNum, productVal.yearlyEquivalent);
      }

      this.purchaseOrderProducts.set(
        productNum,
        productVal.purchaseOrder || false,
      );
    }
  }

  hasProduct = (products: number[]) =>
    products.some((num) => this.products.has(num));

  isFeatureEnabled = (
    feature: OrganizationFeature | MemberFeature,
    productNumbers: number[],
  ) =>
    this.globalFeatures.has(feature) ||
    this.features(productNumbers).has(feature);

  convertToId(product: ProductDesc): number | undefined {
    if (_.isString(product)) {
      if (/^\d+$/.test(product)) {
        return parseInt(product, 10);
      } else {
        return this.productShortNames.get(product as ProductShortName);
      }
    } else {
      return product as number;
    }
  }

  getProductNumber(product: ProductDesc) {
    return this.convertToId(product);
  }

  getPrice(product: ProductDesc) {
    const id = this.convertToId(product);

    return id && this.prices.get(id);
  }

  getInterval(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return this.intervals.get(id);
    }
  }

  isMonthly(product: ProductDesc) {
    return this.getInterval(product) === 'monthly';
  }
  isYearly(product: ProductDesc) {
    return this.getInterval(product) === 'yearly';
  }
  isPerActiveUser(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return !!this.perActiveUserProducts.get(id);
    }
  }
  isPerUser(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return !!this.perUserProducts.get(id);
    }
  }
  isPrebilled(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return this.prebilledProducts.get(id);
    }
  }
  isUpgrade(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return this.upgradeProducts.get(id);
    }
  }

  usesPurchaseOrder(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return this.purchaseOrderProducts.get(id);
    }
  }
  getYearlyEquivalent(product: ProductDesc) {
    const id = this.convertToId(product);

    if (id) {
      return this.yearlyEquivalents.get(id);
    }
  }
  getYearlyDiscountPercent(
    productYearly: ProductDesc,
    productMonthly: ProductDesc,
  ) {
    const priceYearly = this.getPrice(productYearly);
    let priceMonthly = this.getPrice(productMonthly);

    if (priceYearly && priceMonthly) {
      priceMonthly *= 12;

      return Math.round((100 * (priceMonthly - priceYearly)) / priceMonthly);
    }
  }

  features = (productNumbers: number[] = []): ProductFeatureSet =>
    productNumbers.reduce((out, num) => {
      const features = this.productFeatures.get(num);

      // will throw if no features
      assert(
        features,
        `FeatureSet::features called on an unrecognized product: ${productNumbers}`,
      );

      for (const feature of features!) {
        out.add(feature);
      }

      return out;
    }, new Set());
}

export default FeatureSet;



// WEBPACK FOOTER //
// ./app/common/data/feature-set.ts